import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, Menu, LogOut, User, CreditCard, ShieldCheck,
  Flame, Map, Heart, LayoutDashboard, Users, Inbox, Tag, X,
  ArrowLeftRight, CircleHelp, Bookmark, ThumbsUp, MessageCircle, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, userMode, isOnboardedLister, isOnboardedRenter } from "@/context/AuthContext";
import { useNotifications, CountBadge } from "@/context/NotificationsContext";
import { useMarket } from "@/context/LocationContext";
import { useFilters, budgetLabel } from "@/context/FiltersContext";
import BudgetPopover from "@/components/nav/budget_popover";
import { Wordmark } from "@/components/brand/logo_swiprent";

export default function NavTop() {
  const { user, logout, switchMode } = useAuth();
  const { market, stayType, setPickerOpen } = useMarket();
  const { budget, setBudget, clearBudget } = useFilters();
  const navigate = useNavigate();
  const { likes, matches, messages, total } = useNotifications();
  const [open, setOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const menuRef = useRef(null);
  const activeBudget = budgetLabel(budget);

  useEffect(() => {
    function onClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const role = user ? userMode(user) : null;
  const go = (to) => { setOpen(false); navigate(to); };

  // Switch between renter and lister mode from the avatar menu. Entering
  // lister mode requires completed onboarding: an un-onboarded user is routed
  // to the Private vs Realtor chooser (/become-a-lister) BEFORE any mode
  // change. Switching to renter is always immediate.
  const targetMode = role === "lister" ? "renter" : "lister";
  const onboardedLister = isOnboardedLister(user);
  const onboardedRenter = isOnboardedRenter(user);
  const switchLabel =
    targetMode === "lister"
      ? onboardedLister
        ? "Switch to lister mode"
        : "Become a lister"
      : onboardedRenter
        ? "Switch to renter mode"
        : "Become a renter";

  async function flipMode() {
    setOpen(false);
    // Server authoritative: attempt the switch and let the backend decide.
    // The backend allows a switch to lister only when onboarding is complete,
    // otherwise it returns 409 ONBOARDING_REQUIRED and we open the chooser.
    // Switching to renter always succeeds; if the renter has not finished the
    // one-time renter setup we land on the profile form. This avoids relying on
    // possibly stale client onboarding flags (the source of the re-ask bug).
    try {
      const updated = await switchMode(targetMode);
      if (targetMode === "renter" && !isOnboardedRenter(updated || user)) {
        navigate("/profile", { state: { welcome: true } });
        return;
      }
      toast.success(targetMode === "lister" ? "You are in lister mode now." : "You are in renter mode now.");
      navigate(targetMode === "lister" ? "/lister" : "/");
    } catch (error) {
      if (error?.response?.status === 409 &&
          error?.response?.data?.code === "ONBOARDING_REQUIRED") {
        navigate("/become-a-lister", { state: { next: "/lister" } });
        return;
      }
      toast.warning("We could not switch modes. Please try again.");
    }
  }

  return (
    // z-[60]: the avatar dropdown must clear the map page's listing preview
    // card (z-[50]) while staying under every z-[100] modal.
    <header className="sticky top-0 z-[60] border-b border-night-line bg-night">
      <div className="mx-auto flex h-20 max-w-[1360px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-10">
        <Link to="/" aria-label="SwipRent home" className="shrink-0">
          <Wordmark onDark size={22} className="hidden md:inline-flex" />
          <Wordmark onDark size={18} className="md:hidden" />
        </Link>

        {/* Airbnb-style search pill: city and stay segments open the market
            picker, the budget segment opens its own popover, and the lime
            search icon still goes to the map. */}
        <div className="relative flex min-w-0 items-center rounded-full border border-night-line bg-night-surface shadow-hairline transition-shadow duration-200 hover:shadow-ambient">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            aria-label="Choose your market"
            className="flex min-w-0 items-center"
          >
            <span className="truncate py-2.5 pl-5 pr-4 text-sm font-semibold text-night-text">
              {market?.city || "Toronto"}
            </span>
            <span className="hidden h-6 w-px shrink-0 bg-night-line sm:block" />
            <span className="hidden px-4 text-sm font-semibold text-night-text sm:block">
              {stayType === "short" ? "Short stay" : "Long term"}
            </span>
          </button>
          <span className="hidden h-6 w-px shrink-0 bg-night-line md:block" />
          <span className="hidden items-center md:flex">
            <button
              type="button"
              onClick={() => setBudgetOpen((v) => !v)}
              aria-label={activeBudget ? `Budget ${activeBudget}. Change budget` : "Add a budget"}
              className={`py-2.5 pl-4 text-sm transition ${
                activeBudget ? "pr-1 font-semibold text-night-text" : "pr-4 font-medium text-night-sub hover:text-night-text"
              }`}
            >
              {activeBudget || "Add budget"}
            </button>
            {activeBudget && (
              <button
                type="button"
                aria-label="Clear budget"
                onClick={() => clearBudget()}
                className="mr-2 rounded-full p-1 text-night-sub transition hover:bg-night-tint hover:text-night-text"
              >
                <X size={13} strokeWidth={2.5} />
              </button>
            )}
          </span>
          <BudgetPopover
            open={budgetOpen}
            onClose={() => setBudgetOpen(false)}
            budget={budget}
            onApply={setBudget}
            onClear={clearBudget}
          />
          <button
            type="button"
            onClick={() => navigate("/map")}
            aria-label="Search homes on the map"
            className="my-1.5 ml-1 mr-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white"
          >
            <Search size={14} strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <Link
            to="/how-it-works"
            className="hidden rounded-full px-4 py-2.5 text-sm font-semibold text-night-text transition hover:bg-night-surface lg:block"
          >
            How it works
          </Link>
          <Link
            to={role === "lister" ? "/lister" : "/become-a-lister"}
            className="hidden rounded-full px-4 py-2.5 text-sm font-semibold text-night-text transition hover:bg-night-surface lg:block"
          >
            List your place
          </Link>

          {/* Avatar pill */}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              aria-label={total > 0 ? `Open menu, ${total} new notifications` : "Open menu"}
              onClick={() => setOpen((v) => !v)}
              className="relative flex items-center gap-2.5 rounded-full border border-night-line bg-night-surface py-1 pl-3.5 pr-1 transition-shadow duration-200 hover:shadow-ambient"
            >
              <Menu size={16} className="text-night-text" />
              {/* Avatar: the user's real photo when they have one (lister
                  headshot wins in lister mode), initial fallback otherwise. */}
              <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-brand-primary text-xs font-semibold text-white">
                {(() => {
                  const photo =
                    role === "lister"
                      ? user?.lister_photo_url || user?.photo_url
                      : user?.photo_url || user?.lister_photo_url;
                  if (photo) {
                    return <img src={photo} alt="" className="h-full w-full object-cover" />;
                  }
                  return user ? (user.name || user.email || "U").slice(0, 1).toUpperCase() : <User size={15} />;
                })()}
              </span>
              {total > 0 && (
                <CountBadge count={total} className="absolute -right-1 -top-1 ring-2 ring-night" />
              )}
            </button>

            {open && (
              <div className="absolute right-0 mt-2 w-60 overflow-hidden rounded-2xl border border-night-line bg-night-surface py-2 shadow-ambient">
                {user ? (
                  <>
                    <div className="flex items-center justify-between px-4 pb-2 pt-1">
                      <span className="text-xs font-medium text-night-sub">Signed in as</span>
                      <span className="rounded-full bg-night-tint px-2.5 py-0.5 text-xs font-semibold text-brand-primary">
                        {role === "lister" ? "Lister mode" : "Renter mode"}
                      </span>
                    </div>
                    {role === "lister" ? (
                      <>
                        <MenuItem icon={LayoutDashboard} label="Dashboard" onClick={() => go("/lister")} />
                        <MenuItem icon={Users} label="Browse renters" onClick={() => go("/lister/browse")} />
                        <MenuItem icon={Inbox} label="Applicants" count={likes} onClick={() => go("/lister/applicants")} />
                        <MenuItem icon={CalendarDays} label="Calendar" onClick={() => go("/calendar")} />
                        <MenuItem icon={MessageCircle} label="Messages" count={messages} onClick={() => go("/messages")} />
                        {/* Screening is not live yet: locked here exactly
                            like the dashboard card, so no surface disagrees. */}
                        <MenuItem icon={ShieldCheck} label="Screening" soon />
                      </>
                    ) : (
                      <>
                        <MenuItem icon={Flame} label="Swipe homes" onClick={() => go("/swipe")} />
                        <MenuItem icon={Map} label="Map search" onClick={() => go("/map")} />
                        <MenuItem icon={CalendarDays} label="Calendar" onClick={() => go("/calendar")} />
                        <MenuItem icon={Bookmark} label="Saved" onClick={() => go("/saved")} />
                        <MenuItem icon={ThumbsUp} label="Likes" onClick={() => go("/likes")} />
                        <MenuItem icon={Heart} label="Liked you" count={likes} onClick={() => go("/liked-you")} />
                        <MenuItem icon={Users} label="Hosts and agents" onClick={() => go("/hosts")} />
                        <MenuItem icon={Heart} label="Matches" count={matches} onClick={() => go("/matches")} />
                        <MenuItem icon={MessageCircle} label="Messages" count={messages} onClick={() => go("/messages")} />
                      </>
                    )}
                    <div className="my-1.5 border-t border-night-line" />
                    <MenuItem icon={ArrowLeftRight} label={switchLabel} onClick={flipMode} />
                    <MenuItem icon={User} label="My profile" onClick={() => go(role === "lister" ? "/profile/lister" : "/profile")} />
                    <MenuItem icon={CreditCard} label="Pricing and plans" onClick={() => go("/pricing")} />
                    <MenuItem icon={CircleHelp} label="How it works" onClick={() => go("/how-it-works")} />
                    {user.role === "admin" && (
                      <MenuItem icon={ShieldCheck} label="Admin console" onClick={() => go("/admin")} />
                    )}
                    <div className="my-1.5 border-t border-night-line" />
                    <MenuItem
                      icon={LogOut}
                      label="Sign out"
                      onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
                    />
                  </>
                ) : (
                  <>
                    <MenuItem label="Sign up" bold onClick={() => go("/auth/signup")} />
                    <MenuItem label="Log in" onClick={() => go("/auth/login")} />
                    <div className="my-1.5 border-t border-night-line" />
                    <MenuItem icon={Map} label="Explore the map" onClick={() => go("/map")} />
                    <MenuItem icon={CircleHelp} label="How it works" onClick={() => go("/how-it-works")} />
                    <MenuItem icon={Tag} label="Pricing" onClick={() => go("/pricing")} />
                    <MenuItem icon={LayoutDashboard} label="List your place" onClick={() => go("/become-a-lister")} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function MenuItem({ icon: Icon, label, onClick, bold = false, count = 0, soon = false }) {
  // `soon`: feature not live yet — rendered locked (no click) with a Coming
  // soon chip, mirroring the dashboard's locked cards.
  return (
    <button
      type="button"
      onClick={soon ? undefined : onClick}
      disabled={soon}
      className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm transition ${
        soon
          ? "cursor-not-allowed text-night-sub"
          : `hover:bg-night ${bold ? "font-semibold text-night-text" : "font-medium text-night-text"}`
      }`}
    >
      {Icon ? <Icon size={15} className="text-night-sub" /> : null}
      <span className="flex-1">{label}</span>
      {soon ? (
        <span className="rounded-full bg-night-tint px-2 py-0.5 text-[10px] font-semibold text-night-sub">
          Coming soon
        </span>
      ) : (
        <CountBadge count={count} />
      )}
    </button>
  );
}
